import { useCurrentStudentUserShort } from "@/utils/api";
import { Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { Card } from "@repo/ui";

interface Props {
  children: React.ReactNode | React.ReactNode[] | null;
}

export const ToSignUp = ({ allowedAccess = false, onClose }: { allowedAccess?: boolean, onClose?: () => void }) => <Stack
  direction="column"
  alignItems="center"
  justifyContent="center"
  height="100%"
>
  <Card>
    <Stack alignItems="center" spacing={2} textAlign="center" width="100%">
      <Typography variant="h3" fontWeight={700} color="primary">
        {allowedAccess ? "👋 Welcome, Guest!" : "🔒 Zube AI Users Only"}
      </Typography>
      <Typography variant="h6" color="text.secondary" maxWidth={420}>
        {allowedAccess ? (
          <>
            You&apos;re currently browsing as a guest.<br />
            <b>Some information may be hidden</b> or limited because we can&apos;t personalize your experience without an account.<br /><br />
            For the best experience and full access, please <b>create a FREE account</b> or log in. 🚀
          </>
        ) : (
          <>
            This area is for registered users only.<br />
            <b>Why?</b> So we can provide you with tailored information and the best experience possible!<br /><br />
            Please <b>create a FREE account</b> or log in to continue. 🚀
          </>
        )}
      </Typography>
      {
        allowedAccess && <Box>
          <Button variant='contained' color='primary' onClick={onClose}>
            Continue as Guest
          </Button>
        </Box>
      }
      <Stack direction="row" spacing={2} maxWidth="400px" justifyContent="center" width="100%">
        <Box flex={1}>
          <a href="/auth/signup" style={{ textDecoration: 'none' }}>
            <Box
              component="button"
              sx={{
                width: '100%',
                py: 1.5,
                borderRadius: 2,
                background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                fontWeight: 600,
                fontSize: '1.1rem',
                border: 'none',
                cursor: 'pointer',
                transition: 'background 0.2s',
                '&:hover': {
                  background: 'linear-gradient(90deg, #764ba2 0%, #667eea 100%)',
                },
              }}
            >
              ✨ Sign Up Free
            </Box>
          </a>
        </Box>
        <Box flex={1}>
          <a href="/auth/login" style={{ textDecoration: 'none' }}>
            <Box
              component="button"
              sx={{
                width: '100%',
                py: 1.5,
                borderRadius: 2,
                background: 'white',
                color: 'primary.main',
                fontWeight: 600,
                fontSize: '1.1rem',
                border: '2px solid',
                borderColor: 'primary.main',
                cursor: 'pointer',
                transition: 'background 0.2s, color 0.2s',
                '&:hover': {
                  background: 'primary.main',
                  color: 'white',
                },
              }}
            >
              🔑 Log In
            </Box>
          </a>
        </Box>
      </Stack>
      <Typography variant="body2" color="text.disabled" mt={2}>
        It only takes a minute. No spam, ever. 💌
      </Typography>
    </Stack>
  </Card>
</Stack>

export default function AuthWrapper({ children }: Props) {
  const { data: user, isLoading } = useCurrentStudentUserShort();

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><CircularProgress /></Box>;
  if (!user) {
    return <ToSignUp />
  }
  return <>{children}</>;
}